import React, { useEffect, useState } from 'react'
import { compose } from '@cbgms/base/utils/compose'
import { Trans, useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'
import { Timing } from '@cbgms/base/utils/timing'
import CacheItem from 'app-support/cache/components/CacheItem'
import { deleteCacheKeys, getCacheKeys } from 'app-support/cache/state/actions'
import { connect } from 'react-redux'
import { IResponse } from '@cbgms/api'
import { Box, Paper, TextField, Typography } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import DeleteIcon from '@mui/icons-material/Delete'
import { Alert } from '@carsys/ui'

const enhance = compose<Props, {}>(connect(null, { getCacheKeys, deleteCacheKeys }))

interface IMapDispatchToProps {
  getCacheKeys: typeof getCacheKeys
  deleteCacheKeys: typeof deleteCacheKeys
}

type Props = IMapDispatchToProps

export const CacheSettings: React.FC<Props> = ({ getCacheKeys, deleteCacheKeys }) => {
  const { t } = useTranslation()
  const [searchResult, setSearchResults] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchInput, setSearchTerm] = useState('')
  const [searchValue] = useDebounce(searchInput, Timing.keyboardInput)
  useEffect(() => {
    setIsLoading(true)
    const handleSearch = async () => {
      if (searchValue === '') {
        setSearchResults([])
      } else {
        const result = (await getCacheKeys(searchValue)) as any as IResponse<string[]>
        if (result && result.Data) {
          setSearchResults(result.Data)
        } else {
          setSearchResults([])
        }
      }
      setIsLoading(false)
    }
    handleSearch()
  }, [searchValue])

  const handleDeleteCacheKeys = async (passedValue: string) => {
    await deleteCacheKeys(passedValue)
    const result = (await getCacheKeys(searchValue)) as any as IResponse<string[]>
    if (result && result.Data) {
      setSearchResults(result.Data)
    } else {
      setSearchResults([])
    }
  }

  return (
    <>
      <Box sx={{ display: ' flex' }}>
        <TextField
          variant='outlined'
          size='small'
          placeholder={t('global.search')}
          value={searchInput}
          onChange={event => {
            setSearchTerm(event.target.value)
          }}
          inputProps={{
            'data-testid': 'input-cache-searcher'
          }}
          sx={{ flex: '1 1 auto', marginRight: 1 }}
        />
        <LoadingButton
          startIcon={<DeleteIcon />}
          variant='contained'
          color='primary'
          loading={isLoading}
          onClick={() => {
            handleDeleteCacheKeys(searchValue)
          }}
        >
          {t('global.delete')}
        </LoadingButton>
      </Box>
      <Paper sx={{ padding: 2, marginTop: 2 }}>
        {searchInput ? (
          <Typography variant='h6'>
            {t('global.results')}: {searchInput}
          </Typography>
        ) : (
          <div>
            <Typography variant='h6'>{t('cache.header')}</Typography>
            <Trans i18nKey='cache.settingsDescription'>
              <p>From this page you may delete cached content</p>
              <p>
                <strong>Deleting cached items will cause them to be refreshed when trying to resolve their value.</strong>
              </p>
              <p>Keys are case sensitive</p>
            </Trans>
            <ul>
              <li>h?llo matches hello, hallo and hxllo</li>
              <li>h*llo matches hllo and heeeello</li>
              <li>h[ae]llo matches hello and hallo, but not hillo</li>
              <li>h[^e]llo matches hallo, hbllo, ... but not hello</li>
              <li>h[a-b]llo matches hallo and hbllo</li>
            </ul>
            {t('cache.settingsReference')}
            <ul>
              <li>CMS banners: CMS-BANNER*</li>
              <li>RDW: defect-list*</li>
              <li>k8 sessions: k8_order_session_token*</li>
              <li>Haynes vrids: HaynesPro-VRID*</li>
            </ul>
          </div>
        )}
        <div>
          {searchResult && searchResult.length
            ? searchResult.map((r, i) => (
                <CacheItem
                  cacheKey={r}
                  key={i}
                  onDeleteClick={() => {
                    handleDeleteCacheKeys(r)
                  }}
                />
              ))
            : searchValue && <Alert color='muted'>{t('global.search_no_results_found')}</Alert>}
        </div>
      </Paper>
    </>
  )
}

export default enhance(CacheSettings)
